import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Calendar, Building2, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const MyInsurance = () => {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState({ active_count: 0, expired_count: 0 });

  useEffect(() => {
    const fetchMyPolicies = async () => {
      try {
        const response = await api.get('/insurance/my-policies');
        setPolicies(response.data.policies || []);
        setStats({
          active_count: response.data.active_count || 0,
          expired_count: response.data.expired_count || 0
        });
      } catch (err) {
        console.error('Failed to fetch insurance policies:', err);
        toast.error('Failed to load insurance information');
      } finally {
        setLoading(false);
      }
    };

    fetchMyPolicies();
  }, []);

  const getStatusBadge = (policy) => {
    if (policy.is_expired) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Expired
        </span>
      );
    }
    if (policy.days_until_expiry !== null && policy.days_until_expiry <= 30) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Expiring Soon ({policy.days_until_expiry} days)
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        Active
      </span>
    );
  };

  const getPolicyTypeIcon = (type) => {
    const icons = {
      health: '🏥',
      life: '💗',
      accident: '🚑',
      dental: '🦷',
      vision: '👁️',
      disability: '♿',
      other: '📋'
    };
    return icons[type] || '📋';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="spinner w-8 h-8 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="my-insurance">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Insurance</h1>
          <p className="text-sm text-slate-500">View your insurance policies and coverage</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Policies</p>
              <p className="text-2xl font-bold text-green-600">{stats.active_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-slate-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Expired Policies</p>
              <p className="text-2xl font-bold text-slate-400">{stats.expired_count}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policies List */}
      {policies.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Insurance Policies</h3>
            <p className="text-slate-500">
              You don't have any insurance policies mapped to your account yet.
              <br />
              Please contact HR for more information.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {policies.map(policy => (
            <Card key={policy.id} className={`${policy.is_expired ? 'opacity-60' : ''}`} data-testid={`policy-${policy.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getPolicyTypeIcon(policy.policy_type)}</span>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">{policy.plan_name}</h3>
                      <p className="text-sm text-slate-500">{policy.provider_name}</p>
                    </div>
                  </div>
                  {getStatusBadge(policy)}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Policy Number</p>
                    <p className="font-medium">{policy.policy_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Type</p>
                    <p className="font-medium capitalize">{policy.policy_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Coverage Amount</p>
                    <p className="font-medium">₹{policy.coverage_amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Premium</p>
                    <p className="font-medium">₹{policy.premium_amount?.toLocaleString()}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Start Date</p>
                    <p className="font-medium">{policy.start_date ? formatDate(policy.start_date) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">End Date</p>
                    <p className="font-medium">{policy.end_date ? formatDate(policy.end_date) : '-'}</p>
                  </div>
                  {policy.beneficiary_name && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Beneficiary</p>
                      <p className="font-medium">
                        {policy.beneficiary_name}
                        {policy.beneficiary_relationship && ` (${policy.beneficiary_relationship})`}
                      </p>
                    </div>
                  )}
                </div>

                {policy.notes && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-sm text-slate-700">{policy.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help Section */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Need Help?</p>
              <p className="text-sm text-blue-700">
                For questions about your insurance coverage or to update your beneficiary information, 
                please contact the HR department.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyInsurance;
